/*
 * Herbal Monkey - Variant value display
 *
 * Two display surfaces:
 *   1. Inside the variant dropdown — appends "— £0.10/g" (per-unit price)
 *      to each option label so customers can compare value at a glance,
 *      the way UK supermarkets show "£/100g".
 *   2. Next to the main product price — a styled "BEST VALUE" badge that
 *      only appears when the cheapest-per-unit (non-sample) variant is
 *      selected. Updates automatically on variant change.
 *
 * Logic: parses weight/volume from each variant title, computes per-unit
 * price (per g / per ml), identifies the cheapest non-sample as best value.
 *
 * Pure JS, no dependencies. Self-contained styles. Idempotent.
 * Works automatically for every product, present and future.
 */
(function () {
  'use strict';

  var UNIT_RE = /(\d+(?:\.\d+)?)\s*(grams?|gram|g|kilograms?|kilos?|kg|millilitres?|milliliters?|ml|litres?|liters?|l)\b/i;

  function parseQty(value) {
    if (!value) return null;
    var m = String(value).match(UNIT_RE);
    if (!m) return null;
    var qty = parseFloat(m[1]);
    if (!isFinite(qty) || qty <= 0) return null;
    var unit = m[2].toLowerCase();
    if (unit === 'g' || unit.indexOf('gram') === 0) return { qty: qty, base: 'g' };
    if (unit === 'kg' || unit.indexOf('kilo') === 0) return { qty: qty * 1000, base: 'g' };
    if (unit === 'ml' || unit.indexOf('milli') === 0) return { qty: qty, base: 'ml' };
    if (unit === 'l' || unit.indexOf('litre') === 0 || unit.indexOf('liter') === 0) return { qty: qty * 1000, base: 'ml' };
    return null;
  }

  function isSample(text) {
    return /sample/i.test(String(text || ''));
  }

  /*
   * Returns:
   *   {
   *     perUnitByValue: { "100 grams": 0.1495, ... },   // £/g or £/ml
   *     unitLabel: "g" | "ml",
   *     bestValueValue: "200 grams",                    // cheapest non-sample
   *     bestVariantIds: { 12345: true, 67890: true },   // ids that map to bestValueValue
   *     optionKey: "option1"
   *   }
   * or null when the product has no parseable weight/volume option.
   */
  function buildValueMap(variants) {
    if (!Array.isArray(variants) || variants.length < 2) return null;

    var optionKey = null;
    var baseUnit = null;
    var optionKeys = ['option1', 'option2', 'option3'];

    for (var i = 0; i < optionKeys.length; i++) {
      var opt = optionKeys[i];
      var firstUnit = null;
      var parseable = 0;
      var conflicting = false;

      for (var j = 0; j < variants.length; j++) {
        var raw = variants[j][opt];
        if (!raw) continue;
        var p = parseQty(raw);
        if (!p) continue;
        if (firstUnit && p.base !== firstUnit) { conflicting = true; break; }
        firstUnit = p.base;
        parseable++;
      }

      if (!conflicting && parseable >= 2) {
        optionKey = opt;
        baseUnit = firstUnit;
        break;
      }
    }

    if (!optionKey) return null;

    var byValue = {};
    var orderedValues = [];

    for (var k = 0; k < variants.length; k++) {
      var v = variants[k];
      var val = v[optionKey];
      if (!val) continue;
      var parsed = parseQty(val);
      if (!parsed || parsed.base !== baseUnit) continue;

      // Normalise price across two possible input formats:
      //   - Theme JSON (the live case):     995  (integer pence)
      //   - product.json / dev test data:   "9.95" (decimal string)
      // Detect by looking for a decimal point in the original value rather
      // than by magnitude — magnitude detection breaks for products under
      // £10 because 995 pence (£9.95) looks like 995 pounds.
      var rawStr = String(v.price);
      var pricePounds;
      if (rawStr.indexOf('.') !== -1) {
        pricePounds = parseFloat(rawStr);
      } else {
        pricePounds = parseFloat(rawStr) / 100;
      }
      if (!isFinite(pricePounds) || pricePounds <= 0) continue;
      var unitPrice = pricePounds / parsed.qty;

      var existing = byValue[val];
      if (!existing) {
        orderedValues.push(val);
        byValue[val] = {
          value: val,
          qty: parsed.qty,
          unitPrice: unitPrice,
          isSample: isSample(val) || isSample(v.title)
        };
      } else if (unitPrice < existing.unitPrice) {
        existing.unitPrice = unitPrice;
      }
    }

    if (orderedValues.length < 2) return null;

    var perUnitByValue = {};
    for (var v1 in byValue) perUnitByValue[v1] = byValue[v1].unitPrice;

    // Find the cheapest non-sample by unit price.
    var bestValueValue = null;
    var lowest = Infinity;
    for (var v2 in byValue) {
      var e = byValue[v2];
      if (e.isSample) continue;
      if (e.unitPrice < lowest) { lowest = e.unitPrice; bestValueValue = e.value; }
    }

    var bestVariantIds = {};
    if (bestValueValue) {
      for (var n = 0; n < variants.length; n++) {
        var vt = variants[n];
        if (vt[optionKey] === bestValueValue) bestVariantIds[vt.id] = true;
      }
    }

    return {
      perUnitByValue: perUnitByValue,
      unitLabel: baseUnit,
      bestValueValue: bestValueValue,
      bestVariantIds: bestVariantIds,
      optionKey: optionKey
    };
  }

  function detectCurrencySymbol() {
    var priceEl = document.querySelector('.product__price .price__item, .price__item, .price__regular');
    if (priceEl) {
      var text = (priceEl.textContent || '').trim();
      // Leading or trailing non-digit/whitespace cluster.
      var leading = text.match(/^[^\d\s]+/);
      if (leading) return leading[0];
      var trailing = text.match(/[^\d\s.,]+$/);
      if (trailing) return trailing[0];
    }
    return '£';
  }

  function formatUnitPrice(value, symbol, unitLabel) {
    // value is £/unit. 2 decimals reads cleanly across the price range.
    // Only fall back to 3 decimals if 2 would round to 0.00 (sub-1p/unit).
    var formatted = value.toFixed(2);
    if (parseFloat(formatted) === 0 && value > 0) formatted = value.toFixed(3);
    return symbol + formatted + '/' + unitLabel;
  }

  function injectStyles() {
    if (document.getElementById('hm-savings-style')) return;
    var style = document.createElement('style');
    style.id = 'hm-savings-style';
    style.textContent = [
      '.hm-best-value-badge {',
      '  display: inline-block;',
      '  background: #f51000;',
      '  color: #ffffff;',
      '  font-size: 12px;',
      '  font-weight: 700;',
      '  padding: 4px 10px;',
      '  border-radius: 6px;',
      '  margin-left: 10px;',
      '  letter-spacing: 0.6px;',
      '  line-height: 1.4;',
      '  vertical-align: middle;',
      '  text-transform: uppercase;',
      '  white-space: nowrap;',
      '}',
      '.product__price .price__container { display: flex; align-items: center; flex-wrap: wrap; gap: 4px 0; }'
    ].join('\n');
    document.head.appendChild(style);
  }

  /* ---------- in-dropdown per-unit price ---------- */

  function applyToSelects(container, perUnitByValue, unitLabel, optionPosition, currencySymbol) {
    var selects = container.querySelectorAll('select');
    var select = selects[optionPosition - 1];
    if (!select) return;
    var options = select.options;
    for (var i = 0; i < options.length; i++) {
      var opt = options[i];
      if (opt.dataset.hmAnnotated === '1') continue;
      var unitPrice = perUnitByValue[opt.value];
      if (typeof unitPrice !== 'number' || !isFinite(unitPrice)) continue;
      opt.dataset.hmAnnotated = '1';
      opt.textContent = (opt.textContent || '').trim() + ' — ' + formatUnitPrice(unitPrice, currencySymbol, unitLabel);
    }
  }

  // Buttons (kept for completeness if a product ever uses radios).
  function applyToRadios(container, perUnitByValue, unitLabel, optionPosition, currencySymbol) {
    var fieldsets = container.querySelectorAll('fieldset');
    var fieldset = fieldsets[optionPosition - 1];
    if (!fieldset) return;
    var items = fieldset.querySelectorAll('.product-form__item');
    for (var i = 0; i < items.length; i++) {
      var input = items[i].querySelector('input[type="radio"]');
      var label = items[i].querySelector('label');
      if (!input || !label) continue;
      if (label.querySelector('.hm-unit-price')) continue;
      var unitPrice = perUnitByValue[input.value];
      if (typeof unitPrice !== 'number' || !isFinite(unitPrice)) continue;
      var span = document.createElement('span');
      span.className = 'hm-unit-price';
      span.style.fontSize = '11px';
      span.style.color = '#666';
      span.style.marginLeft = '6px';
      span.textContent = formatUnitPrice(unitPrice, currencySymbol, unitLabel);
      label.appendChild(span);
    }
  }

  /* ---------- price-level BEST VALUE badge ---------- */

  function findPriceContainerForVariantContainer(variantContainer) {
    var section = variantContainer.closest('section');
    if (!section) return null;
    var sectionId = variantContainer.dataset.section;
    if (sectionId) {
      var byId = document.getElementById('price-' + sectionId);
      if (byId) return byId;
    }
    return section.querySelector('[id^="price-"]');
  }

  function getCurrentVariantId(variantContainer) {
    var section = variantContainer.closest('section') || document;
    var hidden = section.querySelector('input[name="id"]:not([disabled])') ||
                 section.querySelector('.product-variant-id') ||
                 section.querySelector('input[name="id"]');
    if (hidden && hidden.value) return parseInt(hidden.value, 10);
    var match = window.location.search.match(/[?&]variant=(\d+)/);
    if (match) return parseInt(match[1], 10);
    return null;
  }

  function updateBestValueBadge(priceContainer, bestVariantIds, currentVariantId) {
    if (!priceContainer) return;
    var inner = priceContainer.querySelector('.price__container') || priceContainer;
    var existing = inner.querySelector('.hm-best-value-badge');
    var isBest = !!bestVariantIds[currentVariantId];

    if (!isBest) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('span');
      existing.className = 'hm-best-value-badge';
      existing.textContent = 'BEST VALUE';
      inner.appendChild(existing);
    }
    // Idempotent: if it already exists with the right text, do nothing.
  }

  /* ---------- wiring ---------- */

  function setupVariantContainer(container, currencySymbol) {
    if (container.dataset.hmSavingsApplied === '1') return;
    container.dataset.hmSavingsApplied = '1';

    var jsonScript = container.querySelector('script[type="application/json"]');
    if (!jsonScript) return;
    var variants;
    try { variants = JSON.parse(jsonScript.textContent); } catch (e) { return; }

    var result = buildValueMap(variants);
    if (!result) return;
    var optionPosition = parseInt(result.optionKey.replace('option', ''), 10);
    var tag = container.tagName.toLowerCase();

    if (tag === 'variant-radios') {
      applyToRadios(container, result.perUnitByValue, result.unitLabel, optionPosition, currencySymbol);
    } else if (tag === 'variant-selects') {
      applyToSelects(container, result.perUnitByValue, result.unitLabel, optionPosition, currencySymbol);
    }

    var priceContainer = findPriceContainerForVariantContainer(container);
    if (!priceContainer) return;

    var updating = false;
    var refresh = function () {
      if (updating) return;
      var vid = getCurrentVariantId(container);
      if (vid == null) return;
      updating = true;
      try {
        updateBestValueBadge(priceContainer, result.bestVariantIds, vid);
      } finally {
        setTimeout(function () { updating = false; }, 0);
      }
    };

    refresh();

    // subtree:false so our writes deeper inside .price__container can never
    // retrigger the observer.
    var observer = new MutationObserver(refresh);
    observer.observe(priceContainer, { childList: true, subtree: false });

    container.addEventListener('change', function () {
      setTimeout(refresh, 50);
      setTimeout(refresh, 300);
    });
  }

  function init() {
    injectStyles();
    var currencySymbol = detectCurrencySymbol();
    var nodes = document.querySelectorAll('variant-radios, variant-selects');
    for (var i = 0; i < nodes.length; i++) setupVariantContainer(nodes[i], currencySymbol);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
